/**
 * SeasonalTab Component
 * Displays seasonal trip recommendations based on current season
 */
import React, { useEffect, useState } from 'react';
import { Row, Col, Spinner, Badge, Form } from 'react-bootstrap';
import { discoveryService } from '../../../services/api/discoveryService';
import { SeasonalRecommendation } from '../../../types/discovery.types';
import { DiscoveryTripCard } from '../DiscoveryTripCard';
import './DiscoveryTab.css';

type Season = 'spring' | 'summer' | 'autumn' | 'winter';

const seasonLabels: Record<Season, string> = {
  spring: 'Spring',
  summer: 'Summer',
  autumn: 'Autumn',
  winter: 'Winter'
};

const seasonEmojis: Record<Season, string> = {
  spring: '🌸',
  summer: '☀️',
  autumn: '🍂',
  winter: '❄️'
};

// Helper function to get a month number from a season (for northern hemisphere)
const getMonthFromSeason = (season: Season): number => {
  const seasonMonths: Record<Season, number> = {
    spring: 4, // April
    summer: 7, // July
    autumn: 10, // October
    winter: 1 // January
  };
  return seasonMonths[season];
};

export const SeasonalTab: React.FC = () => {
  const [recommendations, setRecommendations] = useState<
    SeasonalRecommendation[]
  >([]);
  const [currentSeason, setCurrentSeason] = useState<Season>('summer');
  const [selectedSeason, setSelectedSeason] = useState<Season | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCurrentSeason();
  }, []);

  useEffect(() => {
    if (currentSeason) {
      loadRecommendations(selectedSeason || currentSeason);
    }
  }, [selectedSeason, currentSeason]);

  const loadCurrentSeason = async () => {
    try {
      const season = await discoveryService.getCurrentSeason();
      setCurrentSeason(season.season as Season);
    } catch (err) {
      console.error('Failed to get current season:', err);
      // Default to current month-based season
      const month = new Date().getMonth() + 1;
      if (month >= 3 && month <= 5) setCurrentSeason('spring');
      else if (month >= 6 && month <= 8) setCurrentSeason('summer');
      else if (month >= 9 && month <= 11) setCurrentSeason('autumn');
      else setCurrentSeason('winter');
    }
  };

  const loadRecommendations = async (season: Season) => {
    setLoading(true);
    setError(null);
    try {
      const data = await discoveryService.getSeasonalRecommendations(
        getMonthFromSeason(season)
      );
      setRecommendations(data.recommendations);
    } catch (err) {
      setError('Failed to load seasonal recommendations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSeasonChange = (season: Season) => {
    setSelectedSeason(season === currentSeason ? null : season);
  };

  const displaySeason = selectedSeason || currentSeason;

  if (loading) {
    return (
      <div className='discovery-tab-loading'>
        <Spinner
          animation='border'
          variant='primary'
        />
        <p>Loading seasonal recommendations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='discovery-tab-error'>
        <p>{error}</p>
        <button
          className='btn btn-primary'
          onClick={() => loadRecommendations(displaySeason)}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className='discovery-tab'>
      <div className='discovery-tab-header'>
        <h2>
          {seasonEmojis[displaySeason]} {seasonLabels[displaySeason]}{' '}
          Destinations
        </h2>
        <p>Perfect trips for {seasonLabels[displaySeason].toLowerCase()}</p>
      </div>

      <div className='discovery-season-selector'>
        {(Object.keys(seasonLabels) as Season[]).map((season) => (
          <button
            key={season}
            className={`discovery-season-btn ${
              displaySeason === season ? 'active' : ''
            }`}
            onClick={() => handleSeasonChange(season)}
          >
            <span className='discovery-season-emoji'>
              {seasonEmojis[season]}
            </span>
            <span>{seasonLabels[season]}</span>
          </button>
        ))}
      </div>

      {recommendations.length === 0 ? (
        <div className='discovery-tab-empty'>
          <h3>No seasonal recommendations</h3>
          <p>
            Check back later for {seasonLabels[displaySeason].toLowerCase()}{' '}
            trip suggestions!
          </p>
        </div>
      ) : (
        <Row
          xs={1}
          md={2}
          lg={3}
          className='g-4'
        >
          {recommendations.map((recommendation) => (
            <Col key={recommendation.trip._id}>
              <DiscoveryTripCard
                trip={recommendation.trip}
                score={recommendation.seasonalScore.totalScore}
                reason={
                  recommendation.weatherInfo
                    ? `${recommendation.weatherInfo.temperature} - ${recommendation.weatherInfo.conditions}`
                    : undefined
                }
                badge={
                  recommendation.seasonalScore.matchedSeasonalTags.length >
                  0 ? (
                    <Badge bg='info'>
                      {recommendation.seasonalScore.matchedSeasonalTags
                        .slice(0, 2)
                        .join(', ')}
                    </Badge>
                  ) : null
                }
              />
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};
